import React from 'react';
import { Link } from 'react-router-dom';
import { PageBanner } from '../components/shared/PageBanner';
import { Shield, AlertTriangle, BookOpen, HelpCircle, Sparkles, GraduationCap, BarChart3, ArrowRight } from 'lucide-react';
import { SafetyTips } from '../components/education/SafetyTips';
import { CommonScams } from '../components/education/CommonScams';
import { FrequentlyAskedQuestions } from '../components/education/FAQ';
import { TradingBasics } from '../components/education/TradingBasics';
import { useExchangeRates } from '../hooks/useExchangeRates';
import { useMarketInsights } from '../hooks/useEducationContent';

export function Education() {
  const { data: rates } = useExchangeRates();
  const { data: marketInsights, isLoading: insightsLoading } = useMarketInsights(rates);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageBanner
        title="Nigerian Forex Market Rates Education"
        description="Learn about live Naira exchange rate today, Dollar to Naira black market rate trends, and official exchange rate Nigeria policies"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8 bg-gradient-to-r from-emerald-600 to-blue-600 rounded-lg shadow-lg p-8 text-white">
          <div className="flex items-center gap-3 mb-4">
            <GraduationCap className="h-8 w-8" />
            <h2 className="text-2xl font-bold">Comprehensive Learning Hub</h2>
          </div>
          <p className="mb-6 text-emerald-50">
            Access our structured learning paths with AI-powered, personalized content designed specifically for Nigerian forex traders
          </p>
          <Link
            to="/education-hub"
            className="inline-flex items-center gap-2 bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors"
          >
            <Sparkles className="h-5 w-5" />
            Explore Learning Hub
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {marketInsights && marketInsights.length > 0 && (
          <div className="mb-8 bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-6 w-6 text-blue-600" />
              <h3 className="text-xl font-semibold">Market Education Insights</h3>
              <span className="ml-auto px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Live
              </span>
            </div>
            <p className="text-gray-600 mb-4 text-sm">
              Educational insights about current market conditions and economic factors affecting exchange rates
            </p>
            <div className="space-y-3">
              {marketInsights.map((insight: string, index: number) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 leading-relaxed">{insight}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Disclaimer:</strong> These insights are for educational purposes only and do not constitute financial or trading advice. Always conduct your own research and consult with licensed financial advisors.
              </p>
            </div>
          </div>
        )}

        {insightsLoading && (
          <div className="mb-8 bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-6 w-6 text-blue-600 animate-pulse" />
              <h3 className="text-xl font-semibold">Generating Market Insights...</h3>
            </div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-8">
          <TradingBasics />
          <SafetyTips />
          <CommonScams />
          <FrequentlyAskedQuestions />
        </div>
      </div>
    </div>
  );
}